import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  type ExtraWorkedTimeRequestsResponse,
  extraWorkedTimeRequestsResponseSchema,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { type UseQueryResult } from "@tanstack/react-query";
import { startOfDay, subDays } from "date-fns";
import { useMemo } from "react";
import { z } from "zod";

const RESOLVED_REQUESTS_MAX_AGE_DAYS = 30;

const resolvedExtraWorkedTimeRequestsParamsSchema = z.object({
  "filter[type][]": z.nativeEnum(ExtraWorkedTimeType),
  "filter[updatedAt][gte]": z.string(),
});

export function useGetExtraWorkedTimeRequests(
  options: UseGetQueryOptions<ExtraWorkedTimeRequestsResponse> = {}
): UseQueryResult<ExtraWorkedTimeRequestsResponse> {
  const today = startOfDay(new Date()).toISOString();
  const updatedAtFilter = useMemo(
    () => subDays(new Date(today), RESOLVED_REQUESTS_MAX_AGE_DAYS).toISOString(),
    [today]
  );

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/extra-worked-time-requests`,
    requestSchema: resolvedExtraWorkedTimeRequestsParamsSchema,
    responseSchema: extraWorkedTimeRequestsResponseSchema,
    queryParams: {
      "filter[type][]": ExtraWorkedTimeType.FULL_SHIFT,
      "filter[updatedAt][gte]": updatedAtFilter,
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_EXTRA_WORKED_TIME_REQUESTS_FAILURE,
    },
    ...options,
  });
}
